import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Pane, Card, Heading, Text, Button, Badge, Spinner, toaster
} from 'evergreen-ui';
import api from '../services/api';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.get(`/invites/${token}/info`);
        setInfo(res.data);
      } catch (err) {
        toaster.danger(err.response?.data?.detail || 'Invalid invite link');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await api.post(`/invites/${token}/accept`);
      toaster.success(`Joined project "${res.data.project_name}" as ${res.data.role}`);
      navigate('/dashboard');
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" padding={64}>
        <Spinner />
      </Pane>
    );
  }

  if (!info) {
    return (
      <Pane display="flex" justifyContent="center" padding={64}>
        <Card background="tint1" padding={32} maxWidth={480} width="100%" textAlign="center">
          <Heading size={600} marginBottom={16}>Invalid Invite</Heading>
          <Text>This invite link is invalid or has expired.</Text>
          <Button marginTop={24} onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </Pane>
    );
  }

  const roleBadgeColor = {
    owner: 'red', admin: 'orange', member: 'blue', viewer: 'neutral'
  };

  return (
    <Pane display="flex" justifyContent="center" padding={64}>
      <Card background="tint1" padding={32} maxWidth={480} width="100%" elevation={1}>
        <Heading size={600} marginBottom={16}>Project Invitation</Heading>

        <Pane marginBottom={16}>
          <Text size={300} color="muted">Project</Text>
          <Heading size={500}>{info.project_name}</Heading>
        </Pane>

        <Pane marginBottom={16}>
          <Text size={300} color="muted">Role</Text>
          <Pane>
            <Badge color={roleBadgeColor[info.role] || 'neutral'}>
              {info.role.toUpperCase()}
            </Badge>
          </Pane>
        </Pane>

        <Pane marginBottom={24}>
          <Text size={300} color="muted">Invited by</Text>
          <Text display="block">{info.created_by_email}</Text>
        </Pane>

        {info.already_member ? (
          <Pane>
            <Text color="muted">You are already a member of this project.</Text>
            <Button marginTop={16} onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </Pane>
        ) : !info.is_valid ? (
          <Pane>
            <Text color="danger">This invite has expired or reached its usage limit.</Text>
            <Button marginTop={16} onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </Pane>
        ) : (
          <Button
            appearance="primary"
            width="100%"
            height={40}
            onClick={handleAccept}
            isLoading={accepting}
          >
            Join Project
          </Button>
        )}
      </Card>
    </Pane>
  );
};

export default AcceptInvite;
